import { Dropdown, MenuProps, Tooltip, message } from 'antd';
import { Menu } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Toggle from '../../../atoms/Toggle';
import { usePublishProductByAdminMutation } from '../../../../store/services/products';

type Props = {
  sellerId?: string;
  productId: string;
  isPublished?: boolean;
  actions?: {
    canPreviewProduct?: boolean;
    canPromoteProduct?: boolean;
    canEditProduct?: boolean;
    canDuplicateProduct?: boolean;
    canDeleteProduct?: boolean;
    canPublishProduct?: boolean;
  };
  onDeleteClick?: (productId: string) => void;
  onRefresh?: () => void;
};

export default function ProductActionsDropdown({
  sellerId,
  productId,
  isPublished = false,
  actions = {
    canPreviewProduct: true,
    canEditProduct: true,
    canDuplicateProduct: true,
    canDeleteProduct: true,
  },
  onDeleteClick,
  onRefresh,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [publishProduct, { isLoading: publishLoading }] = usePublishProductByAdminMutation();

  const handlePublishToggle = async (checked: boolean) => {
    try {
      const res = await publishProduct({ productId, publish: checked }).unwrap();
      message.success(
        checked
          ? t('sellers.products.publishSuccess')
          : t('sellers.products.unpublishSuccess')
      );
      onRefresh?.();
    } catch (err) {
      message.error(
        checked
          ? t('sellers.products.publishError')
          : t('sellers.products.unpublishError')
      );
    }
  };

  const items: NonNullable<MenuProps['items']> = [
    {
      key: 'title',
      label: (
        <div onClick={(e) => e.stopPropagation()}>
          <span className="text-[#777777] dark:text-gray-400 text-base font-normal px-2 cursor-default block">
            {t('sellers.products.productManagement')}
          </span>
        </div>
      ),
      disabled: true,
      className: '[&.ant-dropdown-menu-item:hover]:!bg-transparent [&.ant-dropdown-menu-item-active]:!bg-transparent !bg-transparent !cursor-default mb-1',
    },
  ];

  items.push({
    key: 'publish',
    className: '[&.ant-dropdown-menu-item:hover]:!bg-transparent [&.ant-dropdown-menu-item-active]:!bg-transparent !bg-transparent !cursor-default',
    label: (
      <div
        className="flex justify-between items-center gap-4 w-full px-2 py-1"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-[#090909] dark:text-white text-base font-semibold">
          {t('sellers.products.publish')}
        </span>
        <Toggle
          checked={isPublished}
          disabled={publishLoading}
          onChange={handlePublishToggle}
          data-testid={`product-publish-toggle-${productId}`}
        />
      </div>
    ),
  });


  if (actions?.canEditProduct !== false) {
    items.push({
      key: 'edit',
      label: (
        <span className="text-[#090909] dark:text-white text-sm font-normal px-2 py-1 block">
          {t('sellers.products.editProduct')}
        </span>
      ),
      onClick: (e) => {
        e.domEvent.stopPropagation();
        const path = sellerId
          ? `/sellers/${sellerId}/profile/add-product?productId=${productId}`
          : `/products/edit/${productId}`;
        navigate(path);
      },
    });
  }

  if (actions?.canDeleteProduct !== false) {
    items.push({
      key: 'delete',
      label: (
        <span className="text-[#090909] dark:text-white text-sm font-normal px-2 py-1 block">
          {t('sellers.products.deleteProduct')}
        </span>
      ),
      onClick: (e) => {
        e.domEvent.stopPropagation();
        onDeleteClick?.(productId);
      },
    });
  }

  const iconTrigger = (
    <div
      className="flex justify-center items-center w-8 h-8 rounded-full transition-colors hover:bg-pink-500/10 cursor-pointer"
      data-testid={`store-products-table-actions-btn-${productId}`}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Menu
        size={20}
        variant="Linear"
        className="transition-colors text-gray-600 dark:text-gray-400 hover:text-pink-500"
      />
    </div>
  );

  return (
    <Dropdown
      menu={{
        items,
        className: 'w-[334px] !p-1 !rounded-[12px] !border !border-[#E7E7E9] dark:!border-gray-700 !shadow-[0_4px_4px_rgba(0,0,0,0.12)] dark:!bg-dark-surface',
      }}
      trigger={['click']}
      placement="bottomRight"
    >
      {iconTrigger}
    </Dropdown>
  );
}
